import React, { useState, useEffect } from "react"
import { Loader2, Pencil } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "src/components/ui/dialog"
import { Button } from "src/components/ui/button"
import { Textarea } from "src/components/ui/textarea"
import { UnitStatusBadge, UNIT_STATUS_STYLES } from "./UnitStatusBadge"
import { DealPicker } from "./DealPicker"

// Statuses for which a unit is being held against a specific deal — picking the
// deal is what makes the block/booking meaningful, so we surface the picker.
const DEAL_LINKED_STATUSES = ["blocked", "booked"]

// Mirror of Unit::TRANSITIONS on the backend so we only offer legal moves.
const TRANSITIONS = {
  available: ["blocked", "booked"],
  blocked: ["available", "booked"],
  booked: ["sold", "available"],
  sold: [],
}

const ACTION_LABELS = {
  available: "Mark available",
  blocked: "Block",
  booked: "Mark booked",
  sold: "Mark sold",
}

export function UnitStatusDialog({ open, unit, onClose, onConfirm, onEdit, isPending }) {
  const [target, setTarget] = useState(null)
  const [reason, setReason] = useState("")
  const [deal, setDeal] = useState(null)
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (open) {
      setTarget(null)
      setReason("")
      setDeal(null)
      setTouched(false)
    }
  }, [open, unit?.id])

  if (!unit) return null

  const options = TRANSITIONS[unit.status] || []
  const reasonRequired = target === "blocked"
  const reasonMissing = reasonRequired && !reason.trim()
  const showDealPicker = DEAL_LINKED_STATUSES.includes(target)

  function handleConfirm() {
    setTouched(true)
    if (!target || reasonMissing) return
    onConfirm({
      status: target,
      reason: reason.trim() || undefined,
      dealId: showDealPicker ? deal?.id : undefined,
    })
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Unit {unit.unit_number}
            <UnitStatusBadge status={unit.status} />
          </DialogTitle>
          <DialogDescription>
            Change this unit's availability. The change is recorded with your name and reason.
          </DialogDescription>
        </DialogHeader>

        {options.length === 0 ? (
          <p className="py-2 text-[13px] text-slate-500">
            A {UNIT_STATUS_STYLES[unit.status]?.label.toLowerCase()} unit cannot change status here.
          </p>
        ) : (
          <div className="space-y-4 py-1">
            <div>
              <p className="mb-1.5 text-[13px] font-medium text-slate-700">New status</p>
              <div className="flex flex-wrap gap-2">
                {options.map((s) => {
                  const active = target === s
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setTarget(s)}
                      className={[
                        "rounded-lg border px-3 py-2 text-[13px] font-medium transition-colors",
                        active
                          ? UNIT_STATUS_STYLES[s].cell + " ring-2 ring-offset-1 ring-slate-300"
                          : "border-slate-200 text-slate-600 hover:bg-slate-50",
                      ].join(" ")}
                    >
                      {ACTION_LABELS[s]}
                    </button>
                  )
                })}
              </div>
            </div>

            {showDealPicker && (
              <div>
                <p className="mb-1.5 text-[13px] font-medium text-slate-700">
                  Deal <span className="font-normal text-slate-400">(optional)</span>
                </p>
                <DealPicker projectId={unit.project_id} value={deal} onChange={setDeal} disabled={isPending} />
              </div>
            )}

            <div>
              <p className="mb-1.5 text-[13px] font-medium text-slate-700">
                Reason{reasonRequired ? "" : " (optional)"}
              </p>
              <Textarea
                rows={2}
                placeholder={reasonRequired ? "Why is this unit being blocked?" : "Add a note (optional)"}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              {touched && reasonMissing && (
                <p className="mt-1 text-[12px] text-red-600">A reason is required to block a unit.</p>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="sm:justify-between">
          {onEdit && (
            <Button variant="outline" onClick={() => onEdit(unit)} disabled={isPending} className="gap-1.5">
              <Pencil className="h-3.5 w-3.5" /> Edit unit
            </Button>
          )}
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose} disabled={isPending}>Cancel</Button>
            <Button
              onClick={handleConfirm}
              disabled={isPending || options.length === 0 || !target}
              className="gap-2"
            >
              {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Apply change
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
